/**
 * Whether a measurement (length / weight band) may be shown for a given booking.
 *
 * Display only. Nothing here feeds pricing, capacity or validation — a booking already holding a
 * measurement these rules would hide keeps working exactly as it did, which is deliberate: a rule
 * added today must not change what an existing booking costs or whether it fits.
 *
 * Kept free of any store import so it can be unit tested as what it is: inputs to a boolean.
 */

export type MeasurementRestrictionMode = 'Only' | 'Except';

/**
 * The per-measurement rules, as they arrive in the clientside-config bundle. Every list is
 * independent, and an **empty** list means "no restriction on this dimension" whatever the mode
 * beside it says — the mode only has meaning once the list has something in it.
 */
export interface MeasurementRestrictionRules {
	passengerTypeRestrictionMode?: MeasurementRestrictionMode;
	passengerTypeIds?: string[];
	cargoCharacteristicRestrictionMode?: MeasurementRestrictionMode;
	cargoCharacteristicIds?: string[];
	userDisplayNameRestrictionMode?: MeasurementRestrictionMode;
	userDisplayNameIds?: string[];
	/**
	 * The escape hatch: offer this measurement when the rules would otherwise leave a picker with
	 * nothing in it. See {@link filterByMeasurementRestrictions} for when that happens and why one
	 * flag covers all three dimensions rather than one per rule.
	 */
	offerWhenNoMatch?: boolean;
}

/**
 * What we know about the booking the picker is being rendered for.
 *
 * The `undefined` / `null` distinction matters and is load-bearing:
 *
 * - **`undefined`** — the dimension does not apply here, so it is skipped entirely. The account-page
 *   vehicle editor has no driver and no trip, so it passes `driverPassengerTypeId: undefined`.
 * - **`null`** — the dimension applies but there is no value (no driver chosen yet, customer has no
 *   user type). An `Only` list cannot be matched by an absent value, so restricted measurements are
 *   hidden; an `Except` list cannot be triggered by one, so they are shown. That matches how
 *   user-type-restricted dynamic booking fields already behave while their lookup resolves.
 */
export interface MeasurementFilterContext {
	driverPassengerTypeId?: string | null;
	/** The vehicle's or trailer's effective characteristics — inherited plus selected, minus removed. */
	cargoCharacteristicIds?: string[] | null;
	userDisplayNameId?: string | null;
}

function allowsValue(
	mode: MeasurementRestrictionMode | undefined,
	restrictedTo: string[] | undefined,
	value: string | null | undefined,
): boolean {
	if (value === undefined) {
		// Dimension not applicable in this context.
		return true;
	}
	if (!restrictedTo || restrictedTo.length === 0) {
		return true;
	}
	const matches = value !== null && restrictedTo.includes(value);
	return mode === 'Except' ? !matches : matches;
}

function allowsAnyValue(
	mode: MeasurementRestrictionMode | undefined,
	restrictedTo: string[] | undefined,
	values: string[] | null | undefined,
): boolean {
	if (values === undefined) {
		return true;
	}
	if (!restrictedTo || restrictedTo.length === 0) {
		return true;
	}
	// A set intersection: the vehicle carries several characteristics and the measurement names
	// several, and one overlap is enough to say the measurement "applies to this vehicle".
	const matches = (values ?? []).some(value => restrictedTo.includes(value));
	return mode === 'Except' ? !matches : matches;
}

/**
 * True when every dimension permits the measurement. Dimensions are ANDed: a measurement restricted
 * to seniors *and* to trucks shows only for a senior driving a truck.
 */
export function isMeasurementAllowed(
	rules: MeasurementRestrictionRules | undefined,
	context: MeasurementFilterContext,
): boolean {
	if (!rules) {
		// Nothing known about this measurement — show it. Failing open is the whole posture here:
		// a measurement missing from the rules map (a stale bundle, an id from a trip payload that
		// the global list does not carry) must not vanish from the picker.
		return true;
	}

	return (
		allowsValue(rules.passengerTypeRestrictionMode, rules.passengerTypeIds, context.driverPassengerTypeId) &&
		allowsAnyValue(
			rules.cargoCharacteristicRestrictionMode,
			rules.cargoCharacteristicIds,
			context.cargoCharacteristicIds,
		) &&
		allowsValue(rules.userDisplayNameRestrictionMode, rules.userDisplayNameIds, context.userDisplayNameId)
	);
}

/**
 * Filters a list of things that carry a measurement id, resolving each one's rules through
 * `lookupRules`.
 *
 * `keepIds` is always retained regardless of the rules — pass the currently selected measurement so
 * a select can still render its own value. Without it, altering a booking made before a rule existed
 * would show an empty picker and read as data loss.
 *
 * **A picker is never returned empty.** An `Only` list cannot be matched by an absent value, and a
 * booking can legitimately have an absent value on any of the three dimensions — no driver yet, a
 * guest with no customer type, a vehicle carrying no characteristics. So an operator who puts an
 * `Only` rule on *every* measurement (senior lengths Only Senior, standard lengths Only Adult) turns
 * such a booking into a dead end rather than a narrowed choice. Two steps catch that, in order:
 *
 * 1. the measurements flagged `offerWhenNoMatch` are offered instead — the operator's own choice of
 *    what a booking the rules cannot place should fall back to,
 * 2. failing that, the unfiltered list, because a picker with nothing in it is worse than one showing
 *    a measurement the rules would rather it did not.
 *
 * Both only ever widen, and only from empty. While anything at all matches, neither runs, so a
 * correctly configured site never sees them.
 */
export function filterByMeasurementRestrictions<T extends { id: string }>(
	measurements: T[] | undefined | null,
	context: MeasurementFilterContext,
	lookupRules: (measurementId: string) => MeasurementRestrictionRules | undefined,
	keepIds: (string | null | undefined)[] = [],
): T[] {
	if (!measurements) {
		return [];
	}

	const allowed = measurements.filter(x => isMeasurementAllowed(lookupRules(x.id), context));

	// Emptiness is judged on what the rules allow, deliberately ignoring `keepIds`. A check-in edit
	// passes the booking's own length as a keep, and a picker offering only the value already in it
	// is the same dead end as an empty one — the operator cannot change it to anything.
	const offered =
		allowed.length > 0
			? allowed
			: measurements.filter(x => lookupRules(x.id)?.offerWhenNoMatch === true);

	if (offered.length === 0) {
		return [...measurements];
	}

	const kept = new Set(keepIds.filter((id): id is string => !!id));
	if (kept.size === 0) {
		return offered;
	}

	// Order is the caller's (the measurement lists arrive sorted by value), so filter the original
	// rather than appending the kept ones to the end.
	const offeredIds = new Set(offered.map(x => x.id));
	return measurements.filter(x => offeredIds.has(x.id) || kept.has(x.id));
}

/**
 * What a picker's selection should become once the rules are applied: a replacement id, or
 * `undefined` for "leave it alone".
 *
 * `undefined` covers three distinct cases, all of which mean the same thing to a caller:
 *
 * - nothing is selected yet — the ordinary defaulting has not run, and is not this function's job,
 * - the selection is still offered,
 * - nothing allowed clears `minimumValue`. A display rule must never leave a booking with no
 *   length, so the existing value stays and the picker keeps rendering it.
 *
 * `minimumValue` is whatever floor the picker already enforces — the cargo / tow-on type's minimum,
 * and when altering an existing booking the length it already had. The replacement clears it, so a
 * rule can never shrink a selection below what the vehicle needs.
 *
 * Within that, the replacement is the smallest allowed measurement **at least as large as the one
 * it replaces**, so moving from an adult to a senior driver turns "Up to 25'" into the senior
 * "Up to 25'" rather than the smallest senior band (#2830). The vehicle has not changed size
 * because someone else is driving it. Only when nothing allowed is that large does it fall back to
 * the smallest allowed measurement that clears `minimumValue` — which is what sends a stranded
 * selection to the operator's `offerWhenNoMatch` choice. The current size is read from
 * `candidates`, so a selection the list does not carry gets no size preference.
 */
export function pickAllowedMeasurement<T extends { id: string; value?: number | null }>(
	currentId: string | undefined | null,
	candidates: T[] | undefined | null,
	context: MeasurementFilterContext,
	lookupRules: (measurementId: string) => MeasurementRestrictionRules | undefined,
	minimumValue: number = 0,
): string | undefined {
	if (!currentId || !candidates) {
		return undefined;
	}

	// Asked without a keep-list on purpose: the question is "would this still be offered?". Note this
	// inherits the never-empty fallback, so on a booking the rules cannot place, the replacement is
	// drawn from the `offerWhenNoMatch` measurements rather than the selection being left stranded on
	// one that is no longer in the list.
	const allowed = filterByMeasurementRestrictions(candidates, context, lookupRules);
	if (allowed.some(x => x.id === currentId)) {
		return undefined;
	}

	const sorted = [...allowed].sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
	const currentValue = candidates.find(x => x.id === currentId)?.value ?? 0;
	const replacement =
		sorted.find(x => (x.value ?? 0) >= Math.max(minimumValue, currentValue)) ??
		sorted.find(x => (x.value ?? 0) >= minimumValue);

	return replacement && replacement.id !== currentId ? replacement.id : undefined;
}
